import { useRouter } from 'next/router';
import { forwardRef } from 'react';
import type { Ref, ButtonHTMLAttributes } from 'react';
import styled, { css } from 'styled-components';

import { th } from '@yoweb/ui/styles/utils/theme';
import { pxToRem } from '@yoweb/ui/styles/utils';
import { Interactive } from '@yoweb/ui/components/typography';

type TabVerticalProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  as?: 'a';
  href?: string;
  isActive?: boolean;
} & DataTestId;

export const TabVertical = forwardRef(
  (
    { children, isActive, disabled = false, ...props }: TabVerticalProps,
    ref: Ref<HTMLButtonElement>,
  ) => {
    const router = useRouter();

    let computedIsActive = isActive ?? false;

    if (props.as === 'a' && isActive === undefined) {
      computedIsActive = router.asPath === props.href;
    }

    return (
      // @ts-ignore TODO improve typing
      <StyledTab
        disabled={disabled}
        ref={ref}
        isActive={computedIsActive}
        data-selected={computedIsActive}
        {...props}
      >
        <Interactive size="lg" weight="semibold">
          {children}
        </Interactive>
        {computedIsActive && <Line />}
      </StyledTab>
    );
  },
);

TabVertical.displayName = 'TabVertical';

const Line = styled.div`
  box-sizing: border-box;
  width: 2px;
  border-radius: 8px;
  height: 32px;
  position: absolute;
  top: 4px;
  left: 0;
  background-color: ${th.getColor('base900')};
`;

const StyledTab = styled.button<TabVerticalProps>`
  position: relative;
  padding: ${pxToRem(9)} ${th.getSpace('normal2')};
  appearance: none;
  background: none;
  border: none;
  color: ${th.getColor('base700')};
  outline: none;
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${th.getColor('base1000')};
    ${Line} {
      background-color: ${th.getColor('base1000')};
    }
  }

  &:focus-visible:not(:disabled)::after {
    border-radius: ${th.getRadii('small')};
    box-shadow: 0 0 0 2px ${th.getColor('base900')};
    content: '';
    height: calc(100% + 2px);
    left: -1px;
    position: absolute;
    top: -1px;
    width: calc(100% + 2px);
  }

  :disabled {
    color: ${th.getColor('base200')};
    cursor: not-allowed;
    ${Line} {
      background-color: ${th.getColor('base200')};
    }
  }

  ${({ isActive }) =>
    isActive &&
    css`
      color: ${th.getColor('base900')};
    `}
`;
